import { Data, UI, Utils } from 'common';
import React, { useRef } from 'react';
import { isInTrialBillingPeriod } from '../subscriptionPlan/planRules';
import getSeatsPopoverContent from './SeatsPopoverContent';

const { WrappedButtonType, Popover, WrappedButton } = UI;
const { BillingPlans } = Utils;
const { PlanIds } = Data;

interface SeatsCounterProps {
	usedSeats: number;
	maxSeats: number;
	includedSeats?: number;
	purchasedSeats?: number;
	seatPrice?: number;
	onAddSeats: () => void;
	onUpgrade: () => void;
}

const SeatsCounter = ({
	usedSeats,
	maxSeats,
	includedSeats,
	purchasedSeats,
	seatPrice,
	onAddSeats,
	onUpgrade,
}: SeatsCounterProps) => {
	const popoverRef = useRef(null);

	const closePopover = () => {
		popoverRef.current?.close();
	};

	const { planDetails } =
		((Utils.Session as any).getAccountInfo() as {
			planDetails?: { planId?: number };
		}) ?? {};
	const isTrialBeforePlanSelected =
		planDetails?.planId === PlanIds.Trial401 &&
		isInTrialBillingPeriod() &&
		BillingPlans.pendingPlanWasAutoSelectedOnRegistration;

	const popoverContent = getSeatsPopoverContent({
		usedSeats,
		maxSeats,
		includedSeats: includedSeats ?? maxSeats,
		purchasedSeats: purchasedSeats ?? 0,
		seatPrice,
		isTrialBeforePlanSelected,
	});

	const handleAddSeats = () => {
		closePopover();
		onAddSeats();
	};

	const handleUpgrade = () => {
		closePopover();
		onUpgrade();
	};

	const popoverActionsAnchor = (
		<a
			className="seats-popover-learn-more"
			href="https://help.stamps.com/hc/en-us/articles/49795851940635-Add-or-Remove-User-Seats"
			target="_blank"
			rel="noopener noreferrer"
		>
			Learn More
		</a>
	);

	const popoverActionsButtons = [
		<WrappedButton
			key="upgrade"
			buttonType={WrappedButtonType.Secondary}
			label="Upgrade"
			icon="upgrade-icon"
			iconPos="left"
			dataqa="seats-popover-upgrade"
			onClick={handleUpgrade}
		/>,
		...(!popoverContent.hideUpdateSeats
			? [
					<WrappedButton
						key="update-seats"
						buttonType={WrappedButtonType.Primary}
						label="Update Seats"
						dataqa="seats-popover-update-seats"
						onClick={handleAddSeats}
					/>,
				]
			: []),
	];

	return (
		<span className="seats-counter" data-qa="seats-counter">
			<span className="seats-counter-value">
				{usedSeats}/{maxSeats}
			</span>
			<span className="seats-counter-label"> Seats used </span>
			<Popover
				ref={popoverRef}
				trigger={
					<i
						className="pi pi-info-circle seats-counter-icon"
						aria-hidden="true"
					/>
				}
				title={popoverContent.title}
				message={popoverContent.message}
				actionsLeft={
					popoverContent.hideUpdateSeats
						? popoverActionsButtons
						: popoverActionsAnchor
				}
				actionsRight={
					popoverContent.hideUpdateSeats ? undefined : popoverActionsButtons
				}
				placement="bottom"
				className="seats-popover"
			/>
		</span>
	);
};

export default SeatsCounter;
